import { signalStore, withMethods } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  addEntity,
  addEntities,
  removeAllEntities,
  removeEntity,
  withEntities,
  removeEntities,
} from '@ngrx/signals/entities';
import * as uuid from 'uuid';
import { ErrorResponse, ErrorWithCode } from '@clientwire/api';

export type ErrorState = {
  id: string;
  code: string;
  title?: string;
  message?: string;
  hasReload?: boolean;
  type?: 'global' | 'inline';
  severity?: 'error' | 'warning' | 'info' | 'success';
  errorLocation?: string;
  errors?: { code: string; message: string }[];
};

export class ResponseError extends Error {
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  override name: 'ResponseError' = 'ResponseError';
  constructor(
    public response: Response,
    msg?: string
  ) {
    super(msg);
  }
}

export const ErrorStore = signalStore(
  { providedIn: 'root' },
  withDevtools('error-store'),
  withEntities<ErrorState>(),
  withMethods((store) => {
    return {
      async addErrorResponse(
        response: unknown,
        type: 'global' | 'inline' = 'inline',
        parse = true,
        errorLocation?: string,
        customTitle?: string,
        customMessage?: string,
        customCode?: string
      ) {
        const errorResponse = response as ResponseError;
        const errorBody: ErrorWithCode | ErrorResponse = parse
          ? await errorResponse.response.json()
          : errorResponse;
        this.addError({
          code: customCode ?? errorBody.code,
          message: customMessage ?? errorBody.message,
          type,
          errorLocation,
          errors: 'errors' in errorBody && errorBody.errors ? errorBody.errors : [],
          ...(customTitle && { title: customTitle }),
        });
      },

      addError(error: Omit<ErrorState, 'id'>) {
        const id = uuid.v4();
        updateState(store, 'Add error', addEntity({ ...error, id }));
        return id;
      },

      addErrors(errors: ErrorState[]) {
        updateState(
          store,
          'Add errors',
          addEntities(errors.map((error) => ({ ...error, id: uuid.v4() })))
        );
      },

      removeError(id: string) {
        updateState(store, 'Remove error', removeEntity(id));
      },

      clearErrors() {
        updateState(store, 'Clear errors', removeAllEntities());
      },

      clearErrorsByCodes(codes: string[]) {
        updateState(
          store,
          'Clear errors by codes',
          removeEntities(
            store
              .entities()
              .filter((error) =>
                codes.some((code) => error.code === code || error.code?.startsWith(code + '.'))
              )
              .map((err) => err.id)
          )
        );
      },

      clearErrorsByLocations(locations: string[]) {
        updateState(
          store,
          'Clear errors by locations',
          removeEntities(
            store
              .entities()
              .filter((error) => locations.includes(error.errorLocation ?? ''))
              .map((err) => err.id)
          )
        );
      },

      getErrorsByLocations(locations: string[]) {
        return store.entities().filter((error) => locations.includes(error.errorLocation ?? ''));
      },

      getErrorsByCodesAndType(codes: string[], type: 'global' | 'inline') {
        return store
          .entities()
          .filter((error) => codes.includes(error.code) && error.type === type);
      },

      getErrorsByCodesAndTypeByPrefix(codes: string[], type: 'global' | 'inline') {
        return store
          .entities()
          .filter(
            (error) =>
              codes.some((code) => error.code === code || error.code?.startsWith(code + '.')) &&
              error.type === type
          );
      },
    };
  })
);
